import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

import { AUDIT_PAGE_SIZE, type AuditFilter, type AuditLogEntry, type AuditLogPage } from './types'

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

function mapEntry(dto: components['schemas']['AuditLogEntryResponse']): AuditLogEntry {
  return {
    id: dto.id,
    occurredAt: dto.occurredAt,
    actorType: dto.actorType,
    actorId: dto.actorId,
    action: dto.action,
    resourceType: dto.resourceType,
    resourceId: dto.resourceId,
    correlationId: dto.correlationId,
    // O backend ja entrega metadata como objeto JSON nao sensivel (ou null).
    metadata: dto.metadata ?? null,
  }
}

/** Lista paginada e filtravel da trilha de auditoria do tenant (dados reais). */
export async function listAuditLog(tenantId: string, filter: AuditFilter): Promise<AuditLogPage> {
  // Com exactOptionalPropertyTypes, so incluimos cada filtro quando presente (nunca `undefined` explicito).
  const query: {
    actorType?: string
    correlationId?: string
    from?: string
    to?: string
    page: number
    pageSize: number
  } = {
    page: filter.page,
    pageSize: AUDIT_PAGE_SIZE,
  }
  if (filter.actorType !== null) {
    query.actorType = filter.actorType
  }
  if (filter.correlationId !== null) {
    query.correlationId = filter.correlationId
  }
  // As datas do input (YYYY-MM-DD) viram limites do dia em UTC: [início do dia, fim do dia] inclusivo.
  if (filter.fromDate !== null) {
    query.from = `${filter.fromDate}T00:00:00.000Z`
  }
  if (filter.toDate !== null) {
    query.to = `${filter.toDate}T23:59:59.999Z`
  }

  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/audit', {
    params: { path: { tenantId }, query },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return {
    items: result.data.items.map(mapEntry),
    totalCount: toNumber(result.data.totalCount),
    page: toNumber(result.data.page),
    pageSize: toNumber(result.data.pageSize),
  }
}

/** Filtros que a exportacao respeita (mesmos da listagem, sem paginacao). */
export interface AuditExportFilter {
  readonly actorType: string | null
  readonly correlationId: string | null
  readonly fromDate: string | null
  readonly toDate: string | null
}

export interface AuditExportResult {
  readonly blob: Blob
  /** Verdadeiro quando havia mais registros do que o teto de exportacao (o chamador avisa o usuario). */
  readonly truncated: boolean
}

/**
 * Baixa a trilha filtrada como CSV. Usa `fetch` cru (o CSV nao e JSON tipado) same-origin — o cookie de
 * sessao vai automaticamente. Le o header de truncamento para avisar quando a exportacao veio cortada.
 */
export async function exportAuditLogCsv(
  tenantId: string,
  filter: AuditExportFilter,
): Promise<AuditExportResult> {
  const params = new URLSearchParams()
  if (filter.actorType !== null) {
    params.set('actorType', filter.actorType)
  }
  if (filter.correlationId !== null) {
    params.set('correlationId', filter.correlationId)
  }
  if (filter.fromDate !== null) {
    params.set('from', `${filter.fromDate}T00:00:00.000Z`)
  }
  if (filter.toDate !== null) {
    params.set('to', `${filter.toDate}T23:59:59.999Z`)
  }

  const queryString = params.toString()
  const url =
    `${window.location.origin}/api/v1/tenants/${encodeURIComponent(tenantId)}/audit/export` +
    (queryString === '' ? '' : `?${queryString}`)

  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'text/csv' },
  })

  if (!response.ok) {
    let problem
    try {
      problem = parseProblemDetails(await response.json(), response.status)
    } catch {
      problem = {
        title: 'Nao foi possivel exportar a auditoria.',
        status: response.status,
        errorCode: 'export_failed',
        correlationId: '',
      }
    }
    throw new ApiError(problem)
  }

  return {
    blob: await response.blob(),
    truncated: response.headers.get('X-Audit-Export-Truncated') === 'true',
  }
}
